import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const LoadingDetail = () => (
  <Box aria-live="polite" aria-busy="true">
    <Typography variant="body2" sx={{ mb: 1 }}>Cargando información del vehículo...</Typography>
    <Skeleton variant="rounded" height={72} />
  </Box>
);

const ErrorDetail = ({ message }) => (
  <Alert severity="error">{message || 'No fue posible cargar la información del vehículo.'}</Alert>
);

const formatDuration = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${days} día/s ${hours} hora/s ${minutes} minuto/s ${seconds} segundo/s`;
};

export const calculateVehicleDetailDuration = (detail) => {
  let total = 0;
  (detail?.estadias || []).forEach((stay) => {
    if (!stay.salida) return;
    const start = new Date(`${stay.entrada.fecha}T${stay.entrada.hora}`);
    const end = new Date(`${stay.salida.fecha}T${stay.salida.hora}`);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) total += end - start;
  });
  (detail?.mensualidades || []).forEach((monthly) => {
    if (!monthly.fecha_cancelacion) return;
    const start = new Date(`${monthly.fecha_inicio}T00:00:00`);
    const end = new Date(monthly.fecha_cancelacion);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) total += end - start;
  });
  return total;
};

export function VehicleClientsDetail({ detailState }) {
  if (detailState.status === 'loading' || detailState.status === 'idle') return <LoadingDetail />;
  if (detailState.status === 'error') return <ErrorDetail message={detailState.error} />;
  const clients = detailState.data?.clientes || [];
  const meta = detailState.data?.meta || {};
  if (clients.length === 0) return <Typography variant="body2">No hay clientes registrados.</Typography>;

  return (
    <>
    <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
      <Table size="small" aria-label="Clientes asociados al vehículo">
        <TableHead>
          <TableRow>
            {['ID', 'NOMBRE', 'APELLIDO', 'TIPO DE DOCUMENTO', 'DOCUMENTO', 'TELÉFONO'].map((label) => (
              <TableCell key={label} sx={{ fontWeight: 'bold' }}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {clients.map((client) => (
            <TableRow key={client.id}>
              <TableCell>{client.id}</TableCell>
              <TableCell>{client.nombre}</TableCell>
              <TableCell>{client.apellidos}</TableCell>
              <TableCell>{client.tipo_documento}</TableCell>
              <TableCell>{client.documento}</TableCell>
              <TableCell>{client.celular}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    {meta.clientes_truncados && <Typography variant="caption">Se muestran los {meta.limite_por_coleccion} clientes más recientes.</Typography>}
    </>
  );
}

export function VehicleActivityDetail({ detailState }) {
  const duration = useMemo(
    () => calculateVehicleDetailDuration(detailState.data),
    [detailState.data]
  );
  if (detailState.status === 'loading' || detailState.status === 'idle') return <LoadingDetail />;
  if (detailState.status === 'error') return <ErrorDetail message={detailState.error} />;

  const stays = detailState.data?.estadias || [];
  const monthly = detailState.data?.mensualidades || [];
  const meta = detailState.data?.meta || {};

  return (
    <Box>
      <Typography variant="body2" sx={{ pb: 2 }}>
        <strong>TIEMPO ACUMULADO EN LOS REGISTROS MOSTRADOS:</strong> {formatDuration(duration)}
      </Typography>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Estadías</Typography>
      {stays.length > 0 ? (
        <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 1 }}>
          {stays.map((stay) => (
            <Box key={stay.id} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, borderBottom: '1px dashed', borderColor: 'divider', py: 0.5 }}>
              <Typography variant="caption">Entrada: {stay.entrada.fecha} {stay.entrada.hora}</Typography>
              <Typography variant="caption">Salida: {stay.salida ? `${stay.salida.fecha} ${stay.salida.hora}` : 'En curso'}</Typography>
            </Box>
          ))}
        </Box>
      ) : <Typography variant="body2">No hay estadías registradas.</Typography>}
      {meta.estadias_truncadas && <Typography variant="caption">Se muestran las {meta.limite_por_coleccion} estadías más recientes.</Typography>}

      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>Mensualidades</Typography>
      {monthly.length > 0 ? (
        <Box sx={{ maxHeight: 150, overflowY: 'auto', pr: 1 }}>
          {monthly.map((item) => (
            <Box key={item.id} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, borderBottom: '1px dashed', borderColor: 'divider', py: 0.5 }}>
              <Typography variant="caption">Inicio: {item.fecha_inicio}</Typography>
              <Typography variant="caption">Cancelación: {item.fecha_cancelacion || 'Activa'}</Typography>
            </Box>
          ))}
        </Box>
      ) : <Typography variant="body2">No hay mensualidades registradas.</Typography>}
      {meta.mensualidades_truncadas && <Typography variant="caption">Se muestran las {meta.limite_por_coleccion} mensualidades más recientes.</Typography>}
    </Box>
  );
}
